import config from '../config';
import { getIdToken, getStoredTokens, storeTokens, clearTokens, isTokenExpired } from './tokenStore';
import { cognitoRefreshSession } from './cognitoAuth';
import { store } from '../store';
import { CLEAR_ALL, SET_TOKEN } from '../store/constants';
import { traceApiCall } from '../services/sessionTelemetry/apiTrace';

// Timed fetch used by every api* helper below — feeds latency/status events
// to session telemetry. Behavior-identical to fetch (same args, same return,
// same throws); tracing itself can never fail a request, and telemetry's own
// endpoints are skip-listed inside traceApiCall so it never times itself.
const tracedFetch = async (input: string, init?: RequestInit): Promise<Response> => {
  const startedAt = Date.now();
  try {
    const res = await fetch(input, init);
    traceApiCall(input, init?.method || 'GET', res.status, Date.now() - startedAt);
    return res;
  } catch (err) {
    traceApiCall(input, init?.method || 'GET', 0, Date.now() - startedAt);
    throw err;
  }
};

/** Sign the user out when the refresh token is definitively invalid. */
async function handleAuthExpiry(): Promise<void> {
  await clearTokens();
  store.dispatch({ type: CLEAR_ALL });
}

// Single-flight guard: when the access token expires, every queued request
// calls getFreshToken() at once. Without this each fires its OWN
// REFRESH_TOKEN_AUTH (the burst of concurrent cognito-idp calls seen at logout)
// and each independently races handleAuthExpiry. Share one refresh — the first
// caller performs it, the rest await the same promise.
let refreshInFlight: Promise<string> | null = null;

/**
 * Returned by getFreshToken when the access token is expired AND the refresh
 * could not be completed for a transient reason (offline, DNS not up yet on a
 * cold start, Cognito throttle).
 *
 * It exists because the previous behaviour — hand back the EXPIRED token and
 * let the request go — quietly became the logout bug. performRefresh
 * deliberately did not sign the user out on a network blip, but returning a
 * stale credential meant the request 401'd, and fetchAccounts classifies a 401
 * as a definitive auth rejection (not transient, since it is not a 5xx). So
 * restoreSession read "we could not reach Cognito for a moment" as "your
 * session is invalid" and signed out a user whose refresh token was good for
 * another 89 days. Once per access-token lifetime — which is 24h, hence
 * roughly daily.
 *
 * Callers return a transient failure instead of sending a credential they
 * already know the server will reject.
 */
export const REFRESH_UNAVAILABLE = '\u0000refresh-unavailable';

/** The shape callers hand back when a refresh could not be attempted. */
const transientFailure = () => ({
  success: false,
  status: 0,
  transient: true,
  message: 'Could not reach the sign-in service. Check your connection and try again.',
});

/**
 * Internal: may return REFRESH_UNAVAILABLE. Used by the api* helpers below so
 * they can return a transient failure instead of sending a credential the
 * server is guaranteed to reject.
 */
async function getFreshTokenOrSignal(): Promise<string> {
  if (!(await isTokenExpired())) return getIdToken();
  if (!refreshInFlight) {
    refreshInFlight = performRefresh().finally(() => {
      refreshInFlight = null;
    });
  }
  return refreshInFlight;
}

/**
 * Public: a usable ID token, or '' when there isn't one.
 *
 * The sentinel is deliberately NOT leaked here. Callers outside this module
 * already guard with `if (!token)`, and handing them a non-empty sentinel would
 * sail through that check straight into an Authorization header.
 */
export async function getFreshToken(): Promise<string> {
  const token = await getFreshTokenOrSignal();
  return token === REFRESH_UNAVAILABLE ? '' : token;
}

async function performRefresh(): Promise<string> {
  try {
    const stored = await getStoredTokens();
    if (!stored?.refreshToken) {
      await handleAuthExpiry();
      return '';
    }

    const result = await cognitoRefreshSession(
      stored.refreshToken,
      stored.refreshClientId || undefined
    );
    if (result.AuthenticationResult) {
      await storeTokens({
        idToken: result.AuthenticationResult.IdToken,
        accessToken: result.AuthenticationResult.AccessToken,
        refreshToken: stored.refreshToken,
        refreshClientId: stored.refreshClientId,
        username: stored.username,
        expiresIn: result.AuthenticationResult.ExpiresIn,
      });
      // Keep redux in step. performRefresh used to update storage only, so
      // `session.token` held the pre-refresh value for the rest of the session
      // — and clinicalSync and useWatchSync read it directly, so they carried
      // on presenting an expired credential long after the app had a good one.
      store.dispatch({ type: SET_TOKEN, payload: result.AuthenticationResult.IdToken });
      return result.AuthenticationResult.IdToken;
    }

    // Cognito returned a response but no AuthenticationResult — sign out
    await handleAuthExpiry();
    return '';
  } catch (err: any) {
    const msg: string = err?.message || '';
    const isAuthError = /NotAuthorizedException|InvalidParameterException|invalid.*(refresh|token)|expired/i.test(msg);
    if (isAuthError) {
      // Refresh token is invalid or expired — sign the user out cleanly
      await handleAuthExpiry();
      return '';
    }
    // Transient. Retry once after a short pause: the overwhelmingly common
    // case is an app cold-starting before the network is actually up, which
    // resolves in a second or two.
    try {
      await new Promise((r) => setTimeout(r, 1500));
      const stored = await getStoredTokens();
      if (stored?.refreshToken) {
        const retry = await cognitoRefreshSession(stored.refreshToken, stored.refreshClientId || undefined);
        if (retry.AuthenticationResult) {
          await storeTokens({
            idToken: retry.AuthenticationResult.IdToken,
            accessToken: retry.AuthenticationResult.AccessToken,
            refreshToken: stored.refreshToken,
            refreshClientId: stored.refreshClientId,
            username: stored.username,
            expiresIn: retry.AuthenticationResult.ExpiresIn,
          });
          store.dispatch({ type: SET_TOKEN, payload: retry.AuthenticationResult.IdToken });
          return retry.AuthenticationResult.IdToken;
        }
      }
    } catch {
      // fall through — still transient
    }
    // Signal that we could not refresh. Deliberately NOT the expired token:
    // sending that produces a 401 the app reads as a definitive rejection.
    return REFRESH_UNAVAILABLE;
  }
}

async function parseResponse(res: Response): Promise<any> {
  const text = await res.text();
  if (!text.trim()) return {};
  try {
    return JSON.parse(text);
  } catch {
    return { success: false, message: res.statusText || 'Invalid response' };
  }
}

// Build the standard "non-2xx" return shape. We pass through `error` and
// `status` so callers can branch on typed error codes (e.g. 'briefings_disabled')
// without resorting to fragile message-text matching, and on the HTTP status
// code itself when the body didn't include an explicit error code.
//
// `errorCode` is the same idea under the other spelling. The backend is not
// consistent: cap denials from check-cap.js and the contacts/convert collision
// return `errorCode`, while older routes return `error`. This shape used to
// forward only `error`, so every `errorCode` the backend sent was dropped here
// and the caller was left matching on prose. Forward both and let callers read
// whichever the route they call actually sends.
function failure(res: Response, data: any) {
  return {
    success: false,
    status: res.status,
    error: data?.error,
    errorCode: data?.errorCode,
    message: data?.message || res.statusText || `HTTP ${res.status}`,
  };
}

// ─── Client API (legacy, /users, /accounts, /auth, /payments, …) ─────────────

export const apiGet = async (path: string): Promise<any> => {
  const token = await getFreshTokenOrSignal();
  if (token === REFRESH_UNAVAILABLE) return transientFailure();
  const res = await tracedFetch(`${config.apiGateway.endpoint}${path}`, {
    headers: { Authorization: token },
  });
  const data = await parseResponse(res);
  if (!res.ok) return failure(res, data);
  return data;
};

export const apiPost = async (path: string, body: any, extraHeaders?: Record<string, string>): Promise<any> => {
  const token = await getFreshTokenOrSignal();
  if (token === REFRESH_UNAVAILABLE) return transientFailure();
  const res = await tracedFetch(`${config.apiGateway.endpoint}${path}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: token,
      ...extraHeaders,
    },
    body: JSON.stringify(body),
  });
  const data = await parseResponse(res);
  if (!res.ok) return failure(res, data);
  return data;
};

export const apiPatch = async (path: string, body: any, extraHeaders?: Record<string, string>): Promise<any> => {
  const token = await getFreshTokenOrSignal();
  if (token === REFRESH_UNAVAILABLE) return transientFailure();
  const res = await tracedFetch(`${config.apiGateway.endpoint}${path}`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      Authorization: token,
      ...extraHeaders,
    },
    body: JSON.stringify(body),
  });
  const data = await parseResponse(res);
  if (!res.ok) return failure(res, data);
  return data;
};

export const apiDelete = async (path: string, body?: object): Promise<any> => {
  const token = await getFreshTokenOrSignal();
  if (token === REFRESH_UNAVAILABLE) return transientFailure();
  const res = await tracedFetch(`${config.apiGateway.endpoint}${path}`, {
    method: 'DELETE',
    headers: { Authorization: token, ...(body != null ? { 'Content-Type': 'application/json' } : {}) },
    ...(body != null ? { body: JSON.stringify(body) } : {}),
  });
  const data = await parseResponse(res);
  if (!res.ok) return failure(res, data);
  return data;
};

// ─── Billing API (/billing/* — Apple IAP + Stripe + partner referrals) ───────

export const apiBillingGet = async (path: string): Promise<any> => {
  const token = await getFreshTokenOrSignal();
  if (token === REFRESH_UNAVAILABLE) return transientFailure();
  const res = await tracedFetch(`${config.billingApi.endpoint}${path}`, {
    headers: { Authorization: token },
  });
  const data = await parseResponse(res);
  if (!res.ok) return failure(res, data);
  return data;
};

export const apiBillingPost = async (path: string, body: any): Promise<any> => {
  const token = await getFreshTokenOrSignal();
  if (token === REFRESH_UNAVAILABLE) return transientFailure();
  const res = await tracedFetch(`${config.billingApi.endpoint}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: token },
    body: JSON.stringify(body),
  });
  const data = await parseResponse(res);
  if (!res.ok) return failure(res, data);
  return data;
};

export const apiBillingDelete = async (path: string, body?: object): Promise<any> => {
  const token = await getFreshTokenOrSignal();
  if (token === REFRESH_UNAVAILABLE) return transientFailure();
  const res = await tracedFetch(`${config.billingApi.endpoint}${path}`, {
    method: 'DELETE',
    headers: { Authorization: token, ...(body != null ? { 'Content-Type': 'application/json' } : {}) },
    ...(body != null ? { body: JSON.stringify(body) } : {}),
  });
  const data = await parseResponse(res);
  if (!res.ok) return failure(res, data);
  return data;
};

/**
 * Unauthenticated GET against the billing service. Used by RegisterScreen to
 * preview-verify a referral code before the user has Cognito tokens. Skips
 * `getFreshToken()` so an unauthenticated session can't accidentally trigger
 * a refresh-token flow that signs the user out.
 */
export const apiBillingGetPublic = async (path: string): Promise<any> => {
  const res = await tracedFetch(`${config.billingApi.endpoint}${path}`);
  const data = await parseResponse(res);
  if (!res.ok) return failure(res, data);
  return data;
};

// ─── Projects API (/projects/* — project routes spun out of client) ──────────
// Identical request shape to apiGet/Post/etc. Switch a caller from the client
// helpers to these helpers + drop the leading `/projects/` from the path —
// e.g. apiGet('/projects/get-all/x/y')  →  apiProjectsGet('/get-all/x/y').

export const apiProjectsGet = async (path: string): Promise<any> => {
  const token = await getFreshTokenOrSignal();
  if (token === REFRESH_UNAVAILABLE) return transientFailure();
  const res = await tracedFetch(`${config.projectsApi.endpoint}${path}`, {
    headers: { Authorization: token },
  });
  const data = await parseResponse(res);
  if (!res.ok) return failure(res, data);
  return data;
};

// ─── Provenant API (/provenant/* — proof-of-effect verdicts for chat actions) ─
export const apiProvenantGet = async (path: string): Promise<any> => {
  const token = await getFreshTokenOrSignal();
  if (token === REFRESH_UNAVAILABLE) return transientFailure();
  const res = await tracedFetch(`${config.provenantApi.endpoint}${path}`, {
    headers: { Authorization: token },
  });
  const data = await parseResponse(res);
  if (!res.ok) return failure(res, data);
  return data;
};

export const apiProjectsPost = async (path: string, body: any, extraHeaders?: Record<string, string>): Promise<any> => {
  const token = await getFreshTokenOrSignal();
  if (token === REFRESH_UNAVAILABLE) return transientFailure();
  const res = await tracedFetch(`${config.projectsApi.endpoint}${path}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: token,
      ...extraHeaders,
    },
    body: JSON.stringify(body),
  });
  const data = await parseResponse(res);
  if (!res.ok) return failure(res, data);
  return data;
};

export const apiProjectsPatch = async (path: string, body: any, extraHeaders?: Record<string, string>): Promise<any> => {
  const token = await getFreshTokenOrSignal();
  if (token === REFRESH_UNAVAILABLE) return transientFailure();
  const res = await tracedFetch(`${config.projectsApi.endpoint}${path}`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      Authorization: token,
      ...extraHeaders,
    },
    body: JSON.stringify(body),
  });
  const data = await parseResponse(res);
  if (!res.ok) return failure(res, data);
  return data;
};

export const apiProjectsDelete = async (path: string, body?: object): Promise<any> => {
  const token = await getFreshTokenOrSignal();
  if (token === REFRESH_UNAVAILABLE) return transientFailure();
  const res = await tracedFetch(`${config.projectsApi.endpoint}${path}`, {
    method: 'DELETE',
    headers: { Authorization: token, ...(body != null ? { 'Content-Type': 'application/json' } : {}) },
    ...(body != null ? { body: JSON.stringify(body) } : {}),
  });
  const data = await parseResponse(res);
  if (!res.ok) return failure(res, data);
  return data;
};

// ─── Wellness API (/wellness/* — wellness + health groups spun out of client) ─
// Identical request shape to apiGet/Post; only the base URL differs (ED-2574).
// Wellness-group paths drop their old /wellness prefix —
//   apiGet('/wellness/snapshot/x/y/z')  →  apiWellnessGet('/snapshot/x/y/z')
// — while health-group paths keep theirs (public URLs are /wellness/health/*):
//   apiGet('/health/data/x/y/z')        →  apiWellnessGet('/health/data/x/y/z')

export const apiWellnessGet = async (path: string): Promise<any> => {
  const token = await getFreshTokenOrSignal();
  if (token === REFRESH_UNAVAILABLE) return transientFailure();
  const res = await tracedFetch(`${config.wellnessApi.endpoint}${path}`, {
    headers: { Authorization: token },
  });
  const data = await parseResponse(res);
  if (!res.ok) return failure(res, data);
  return data;
};

export const apiWellnessPost = async (path: string, body: any, extraHeaders?: Record<string, string>): Promise<any> => {
  const token = await getFreshTokenOrSignal();
  if (token === REFRESH_UNAVAILABLE) return transientFailure();
  const res = await tracedFetch(`${config.wellnessApi.endpoint}${path}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: token,
      ...extraHeaders,
    },
    body: JSON.stringify(body),
  });
  const data = await parseResponse(res);
  if (!res.ok) return failure(res, data);
  return data;
};

// ─── Jobs API (/jobs/cron/* — cron group spun out of client, ED-2574) ────────
// Only the admin-triggered cron endpoints are called from the app
// (patient-agent, weekly-digest send-test), so POST is the only helper.

export const apiJobsPost = async (path: string, body: any, extraHeaders?: Record<string, string>): Promise<any> => {
  const token = await getFreshTokenOrSignal();
  if (token === REFRESH_UNAVAILABLE) return transientFailure();
  const res = await tracedFetch(`${config.jobsApi.endpoint}${path}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: token,
      ...extraHeaders,
    },
    body: JSON.stringify(body),
  });
  const data = await parseResponse(res);
  if (!res.ok) return failure(res, data);
  return data;
};

// ─── Partners service helpers ────────────────────────────────────────────────
//
// The partners service lives at {stage}-api.eleplan.{org,com}/partners — a
// sibling of /client. These helpers mirror apiGet / Post / Patch / Delete
// exactly; only the base URL differs. Public partners endpoints (/lookup-npi,
// /logo, /referral/verify) accept a `noAuth: true` option so the helpers
// skip the Authorization header for the unauthenticated case — the JWT
// would be harmless on those routes (the API Gateway authorizer isn't
// attached), but skipping the token-refresh round-trip cuts ~50ms off
// register-screen-cold-start latency.

// These four use failure() on a non-2xx like every other helper in this file.
// They used to hand-build `{ success, message }` instead, which silently
// dropped `errorCode` and `status` — and the partners API is the one service
// whose errors are a fixed vocabulary the caller is meant to branch on
// (SELF_SERVE_DISABLED, MAX_ACCOUNTS, PARTNER_DISCOUNT_NOT_CONFIGURED, …).
// The referral-link telemetry read `errorCode` off these responses to report
// WHY a partner link failed, so every real failure was recorded as
// reason: 'unknown' — the field existed, was always the same value, and told
// nobody anything.
const partnersUrl = (path: string) => `${config.partnersApi.endpoint}${path}`;

export interface PartnersFetchOptions {
  noAuth?: boolean;
  extraHeaders?: Record<string, string>;
}

export const apiPartnersGet = async (path: string, opts: PartnersFetchOptions = {}): Promise<any> => {
  const headers: Record<string, string> = { ...(opts.extraHeaders || {}) };
  if (!opts.noAuth) {
    const token = await getFreshTokenOrSignal();
    if (token === REFRESH_UNAVAILABLE) return transientFailure();
    headers.Authorization = token;
  }
  const res = await tracedFetch(partnersUrl(path), { headers });
  const data = await parseResponse(res);
  if (!res.ok) return failure(res, data);
  return data;
};

export const apiPartnersPost = async (path: string, body: any, opts: PartnersFetchOptions = {}): Promise<any> => {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(opts.extraHeaders || {}),
  };
  if (!opts.noAuth) {
    const token = await getFreshTokenOrSignal();
    if (token === REFRESH_UNAVAILABLE) return transientFailure();
    headers.Authorization = token;
  }
  const res = await tracedFetch(partnersUrl(path), {
    method: 'POST',
    headers,
    body: JSON.stringify(body),
  });
  const data = await parseResponse(res);
  if (!res.ok) return failure(res, data);
  return data;
};

export const apiPartnersPatch = async (path: string, body: any, opts: PartnersFetchOptions = {}): Promise<any> => {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(opts.extraHeaders || {}),
  };
  if (!opts.noAuth) {
    const token = await getFreshTokenOrSignal();
    if (token === REFRESH_UNAVAILABLE) return transientFailure();
    headers.Authorization = token;
  }
  const res = await tracedFetch(partnersUrl(path), {
    method: 'PATCH',
    headers,
    body: JSON.stringify(body),
  });
  const data = await parseResponse(res);
  if (!res.ok) return failure(res, data);
  return data;
};

export const apiPartnersDelete = async (path: string, body?: object, opts: PartnersFetchOptions = {}): Promise<any> => {
  const headers: Record<string, string> = { ...(opts.extraHeaders || {}) };
  if (!opts.noAuth) {
    const token = await getFreshTokenOrSignal();
    if (token === REFRESH_UNAVAILABLE) return transientFailure();
    headers.Authorization = token;
  }
  if (body != null) headers['Content-Type'] = 'application/json';
  const res = await tracedFetch(partnersUrl(path), {
    method: 'DELETE',
    headers,
    ...(body != null ? { body: JSON.stringify(body) } : {}),
  });
  const data = await parseResponse(res);
  if (!res.ok) return failure(res, data);
  return data;
};

// ─── Admin API (/admin/* — engineering-admin service) ────────────────────────
// Server-side gated by isAdmin (memberships.permissions includes 'eleplan-admin').
// Mirrors apiGet/Post/etc. — only the base URL differs.

export const apiAdminGet = async (path: string): Promise<any> => {
  const token = await getFreshTokenOrSignal();
  if (token === REFRESH_UNAVAILABLE) return transientFailure();
  const res = await tracedFetch(`${config.adminApi.endpoint}${path}`, {
    headers: { Authorization: token },
  });
  const data = await parseResponse(res);
  if (!res.ok) return failure(res, data);
  return data;
};

export const apiAdminPost = async (path: string, body: any, extraHeaders?: Record<string, string>): Promise<any> => {
  const token = await getFreshTokenOrSignal();
  if (token === REFRESH_UNAVAILABLE) return transientFailure();
  const res = await tracedFetch(`${config.adminApi.endpoint}${path}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: token,
      ...extraHeaders,
    },
    body: JSON.stringify(body),
  });
  const data = await parseResponse(res);
  if (!res.ok) return failure(res, data);
  return data;
};

export const apiAdminPatch = async (path: string, body: any, extraHeaders?: Record<string, string>): Promise<any> => {
  const token = await getFreshTokenOrSignal();
  if (token === REFRESH_UNAVAILABLE) return transientFailure();
  const res = await tracedFetch(`${config.adminApi.endpoint}${path}`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      Authorization: token,
      ...extraHeaders,
    },
    body: JSON.stringify(body),
  });
  const data = await parseResponse(res);
  if (!res.ok) return failure(res, data);
  return data;
};

export const apiAdminPut = async (path: string, body: any, extraHeaders?: Record<string, string>): Promise<any> => {
  const token = await getFreshTokenOrSignal();
  if (token === REFRESH_UNAVAILABLE) return transientFailure();
  const res = await tracedFetch(`${config.adminApi.endpoint}${path}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      Authorization: token,
      ...extraHeaders,
    },
    body: JSON.stringify(body),
  });
  const data = await parseResponse(res);
  if (!res.ok) return failure(res, data);
  return data;
};

export const apiAdminDelete = async (path: string, body?: object): Promise<any> => {
  const token = await getFreshTokenOrSignal();
  if (token === REFRESH_UNAVAILABLE) return transientFailure();
  const res = await tracedFetch(`${config.adminApi.endpoint}${path}`, {
    method: 'DELETE',
    headers: { Authorization: token, ...(body != null ? { 'Content-Type': 'application/json' } : {}) },
    ...(body != null ? { body: JSON.stringify(body) } : {}),
  });
  const data = await parseResponse(res);
  if (!res.ok) return failure(res, data);
  return data;
};

// ─── Cadence API (/cadence/* — federation API for the Cadence macOS app) ────
// Most callers will be the Cadence native client (separate repo), but the
// mobile app reads /cadence/entitlements/* to surface enabled-flow chips
// in the account settings screen.

export const apiCadenceGet = async (path: string): Promise<any> => {
  const token = await getFreshTokenOrSignal();
  if (token === REFRESH_UNAVAILABLE) return transientFailure();
  const res = await tracedFetch(`${config.cadenceApi.endpoint}${path}`, {
    headers: { Authorization: token },
  });
  const data = await parseResponse(res);
  if (!res.ok) return failure(res, data);
  return data;
};

export const apiCadencePost = async (path: string, body: any, extraHeaders?: Record<string, string>): Promise<any> => {
  const token = await getFreshTokenOrSignal();
  if (token === REFRESH_UNAVAILABLE) return transientFailure();
  const res = await tracedFetch(`${config.cadenceApi.endpoint}${path}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: token,
      ...extraHeaders,
    },
    body: JSON.stringify(body),
  });
  const data = await parseResponse(res);
  if (!res.ok) return failure(res, data);
  return data;
};

export const apiCadencePatch = async (path: string, body: any, extraHeaders?: Record<string, string>): Promise<any> => {
  const token = await getFreshTokenOrSignal();
  if (token === REFRESH_UNAVAILABLE) return transientFailure();
  const res = await tracedFetch(`${config.cadenceApi.endpoint}${path}`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      Authorization: token,
      ...extraHeaders,
    },
    body: JSON.stringify(body),
  });
  const data = await parseResponse(res);
  if (!res.ok) return failure(res, data);
  return data;
};

export const apiCadencePut = async (path: string, body: any, extraHeaders?: Record<string, string>): Promise<any> => {
  const token = await getFreshTokenOrSignal();
  if (token === REFRESH_UNAVAILABLE) return transientFailure();
  const res = await tracedFetch(`${config.cadenceApi.endpoint}${path}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      Authorization: token,
      ...extraHeaders,
    },
    body: JSON.stringify(body),
  });
  const data = await parseResponse(res);
  if (!res.ok) return failure(res, data);
  return data;
};

export const apiCadenceDelete = async (path: string, body?: object): Promise<any> => {
  const token = await getFreshTokenOrSignal();
  if (token === REFRESH_UNAVAILABLE) return transientFailure();
  const res = await tracedFetch(`${config.cadenceApi.endpoint}${path}`, {
    method: 'DELETE',
    headers: { Authorization: token, ...(body != null ? { 'Content-Type': 'application/json' } : {}) },
    ...(body != null ? { body: JSON.stringify(body) } : {}),
  });
  const data = await parseResponse(res);
  if (!res.ok) return failure(res, data);
  return data;
};
